import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { createPortal } from "react-dom";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../data/authStore";
import { useChainStore } from "../data/chainStore";
import { computePipelineValue } from "../data/pipelineValue";
import { isActiveDeal, needsAttentionDeal } from "../data/dealFilters";
import { ShellContext } from "./global/ShellContext";
import { toast } from "sonner";
import {
  Radar, Briefcase, GitBranch, FileText,
  Users, ShieldCheck,
  Users2, Bell, Settings,
  Menu, X, ChevronDown, ChevronsLeft, ChevronsRight, LogOut,
  Building2, MoreHorizontal, CalendarPlus, Home,
  Link2, BarChart3, Plug, ListChecks, LayoutGrid, Sparkles,
  TrendingUp, Workflow, Activity, Clock, Monitor, Inbox,
  CalendarDays,
} from "lucide-react";
import { OrgSwitcher } from "./team/OrgSwitcher";
import { ChainFlowLogo } from "./brand/ChainFlowLogo";
import { useTodaySignal } from "../hooks/useTodaySignal";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow OS — Universal Operational Sidebar V2 (Stratford build)
   ───────────────────────────────────────────────────────────────────
   Deep cinematic navy shell · subtle ambient violet glow · grouped
   sectioned navigation (CORE / OPERATIONS / SYSTEM) · premium active
   state with left energy indicator · workspace switcher card and
   presence-aware user chip at the bottom.

   All routes + data-testids preserved from V1 (via the legacy testid
   bridge) so existing tests and deep links remain stable.
   ─────────────────────────────────────────────────────────────────── */

/* ─── Sidebar sizing constants (iter-180) ─────────────────────────
 *   DEFAULT — 260px : standard operational mode, full labels.
 *   COMPACT — 76 px : immersive rail, icons only with tooltips.
 * iter-180 narrowed the default from 272 → 260 per the canonical
 * reference (Apple × Stripe × Linear restraint — every pixel earns
 * its keep). Width transitions use a calm cubic-bezier spring tuned
 * for premium feel.
 */
const SIDEBAR_W = 260;
const SIDEBAR_COMPACT_W = 76;
/* iter-233 — Calm breathing strip between the sidebar's right edge
 * and the main content canvas. Published as part of `--cf-sidebar-w`
 * so every page that consumes the variable gets the gutter "for free"
 * without each page having to hand-roll padding. 28px reads as
 * intentional whitespace at desktop widths without sacrificing
 * usable operational space. */
const CONTENT_GUTTER = 28;
/* Legacy export kept so back-compat callers don't break. */
const SIDEBAR_COLLAPSED_W = SIDEBAR_COMPACT_W;
/* New canonical storage key — `cf-sidebar-mode` ∈ {"default","compact"}.
 * The old `cf:sidebar:collapsed` key is read once on first mount for
 * a calm migration, then superseded.
 */
const STORAGE_KEY_MODE = "cf-sidebar-mode";
const STORAGE_KEY_LEGACY_COLLAPSED = "cf:sidebar:collapsed";
/* Session flag — set the moment the operator manually toggles. While
 * present, automatic Deal Room immersion is suppressed for the rest
 * of the session (until the tab is closed). */
const SESSION_KEY_USER_OVERRIDE = "cf-sidebar-user-override";

/* ─── Atmosphere — calm cinematic background (iter-293 refinement) ─
 * Deep midnight navy base · two diffused cyan/purple ambient blooms
 * · no DNA graphics, no network lines, no decorative streaks.
 * Mission-critical, restrained, premium — inspired by Linear,
 * Stripe and Apple. The brief: ChainFlow should feel like
 * infrastructure for property transactions, not a SaaS dashboard.
 *
 * iter-163 (preserved): glow intensity dampens ~20% when in
 * compact mode so the immersive rail feels lighter against a
 * wider working canvas.
 */
const Atmosphere = ({ compact = false }) => {
  const dim = compact ? 0.8 : 1; // ~20% reduction per the brief
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* Layer 1 — deep midnight navy base (#040814 per brief) */}
      <span
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #040814 0%, #050A18 50%, #03060F 100%)",
        }}
      />
      {/* Layer 2 — soft top cyan ambient bloom */}
      <span
        className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full blur-[140px] transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle, rgba(34,211,238,${0.10 * dim}), transparent 70%)`,
          opacity: dim,
        }}
      />
      {/* Layer 3 — soft bottom purple ambient bloom */}
      <span
        className="absolute -bottom-28 -right-24 w-[380px] h-[380px] rounded-full blur-[140px] transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle, rgba(139,92,246,${0.12 * dim}), transparent 70%)`,
          opacity: dim,
        }}
      />
      {/* Layer 4 — readability vignette on the left (gentle) */}
      <span
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(3,5,12,0.45) 0%, rgba(3,5,12,0.12) 30%, transparent 75%)",
        }}
      />
      {/* Layer 5 — right-edge violet rim (hairline) */}
      <span
        className="absolute top-0 right-0 bottom-0 w-px"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(167,139,250,0.22) 30%, rgba(99,102,241,0.14) 70%, transparent 100%)",
        }}
      />
    </div>
  );
};

/* ─── Section label — premium uppercase rhythm (iter-180) ─────────
 *  Reference shows three calm section headings (OPERATIONS /
 *  ORGANISATION / SYSTEM) replacing the iter-162 hairline dividers.
 *  Tight letter-spacing, restrained slate-500 tone, never shouts.
 */
const SectionLabel = ({ label, collapsed }) => {
  if (collapsed) {
    return (
      <li className="py-2.5" aria-hidden>
        <span
          className="block h-px mx-3"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
          }}
        />
      </li>
    );
  }
  return (
    <li
      className="pt-4 pb-1.5 px-3 [@media(max-height:900px)]:pt-2 [@media(max-height:900px)]:pb-0.5"
      data-testid={`sidebar-section-${label.toLowerCase()}`}
    >
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-slate-500/65">
        {label}
      </span>
    </li>
  );
};

/* ─── Section divider — legacy fallback (kept for back-compat) ── */
const SectionDivider = ({ collapsed }) => (
  <li className={`${collapsed ? "" : "px-4"} py-3`} aria-hidden>
    <span
      className="block h-px"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
      }}
    />
  </li>
);

/* ─── Nav item — iter-180 premium illuminated active card ────────
 *  Reference shows a chunkier purple card on active (not the
 *  iter-162 ultra-subtle glass). Stronger surface, soft outer
 *  glow, slight elevation — still elegant, never neon.
 *  Inactive items remain low-contrast slate hovering to white.
 *
 *  iter-293 — `hero` flag promotes the row to the operator's
 *  primary destination card (Deal Rooms) regardless of active
 *  state: blue → violet gradient surface, premium glass treatment,
 *  illuminated edge, inline `25 Active · 6 Attention` subtitle.
 *
 *  iter-308 — Final polish (no redesign): hero card slimmed
 *  further, glow trimmed, sidebar rebalanced to feel midnight
 *  navy with the hero as the lone brand accent. Nav row height
 *  tightened so more items fit on screen, profile card
 *  proportionally smaller with a slightly tighter avatar.
 *  Pipeline KPI weight reduced. Wordmark renders as pure white
 *  (#FFFFFF); the gradient lives only on the icon glyph.
 */
// Non-interactive placeholder row (no link, no route) for a surface that isn't built yet.
const ComingSoonNavItem = ({ label, Icon, testid, collapsed }) => (
  <div
    data-testid={testid}
    aria-disabled="true"
    title={collapsed ? `${label} · Coming soon` : undefined}
    className={`relative flex items-center select-none cursor-default ${
      collapsed ? "justify-center px-0 h-10 w-10 mx-auto" : "gap-3 h-[36px] px-3.5 [@media(max-height:900px)]:h-[32px]"
    } rounded-xl text-[13px] font-medium tracking-[-0.005em] text-slate-500`}
  >
    <Icon size={16} strokeWidth={1.8} className="shrink-0 text-slate-500" aria-hidden />
    {!collapsed && (
      <>
        <span data-cf-nav-label className="truncate">{label}</span>
        <span
          data-cf-nav-label
          data-testid={`${testid}-coming-soon`}
          className="-ml-1 shrink-0 text-[9.5px] font-semibold uppercase tracking-[0.08em] px-1.5 h-[18px] inline-flex items-center rounded-full text-slate-400 ring-1 ring-white/10 bg-white/[0.04]"
        >
          Coming soon
        </span>
      </>
    )}
  </div>
);

const NavItem = ({
  to, label, Icon, badge, secondaryLine, subtleLine, subtleTone,
  hero = false, end, testid, collapsed, attention = false,
}) => (
  <NavLink
    to={to}
    end={!!end}
    data-testid={testid}
    title={collapsed ? label : undefined}
    aria-label={label}
    className={({ isActive }) =>
      `group relative flex items-center ${
        collapsed
          ? "justify-center px-0 h-10 w-10 mx-auto"
          : hero
            ? "gap-3 px-3 py-2 min-h-[46px] [@media(max-height:900px)]:min-h-[42px] [@media(max-height:900px)]:py-1.5"
            : (subtleLine
                ? "gap-3 min-h-[38px] py-1 px-3 [@media(max-height:900px)]:min-h-[34px] [@media(max-height:900px)]:py-0.5"
                : "gap-3 h-[36px] px-3.5 [@media(max-height:900px)]:h-[32px]")
      } rounded-xl text-[13px] font-medium tracking-[-0.005em] transition-colors duration-200 outline-none focus-visible:outline-none focus-visible:rounded-xl focus-visible:ring-1 focus-visible:ring-[#00C6FF]/70 ${
        isActive ? "text-white" : "text-slate-300 hover:text-white hover:bg-white/[0.05] focus-visible:bg-white/[0.05]"
      }`
    }
    style={({ isActive }) => (isActive
      // Selected: blue→purple tint + fine border, no glow (only the current section).
      ? {
          background: "linear-gradient(135deg, rgba(59,130,246,0.16) 0%, rgba(101,54,232,0.24) 100%)",
          border: "1px solid rgba(101,54,232,0.55)",
        }
      : { background: "transparent", border: "1px solid transparent" })}
  >
    {({ isActive }) => (
      <>
        {isActive && (
          <span
            aria-hidden
            data-testid={testid ? `${testid}-active-marker` : undefined}
            className={`absolute ${collapsed ? "left-0.5 top-2 bottom-2" : "left-1.5 top-2.5 bottom-2.5"} w-[3px] rounded-full`}
            style={{ background: "#00C6FF" }}
          />
        )}
        <Icon
          size={hero ? 17 : 16}
          strokeWidth={isActive ? 2.1 : 1.8}
          className={`shrink-0 transition-colors ${
            isActive ? "text-white" : "text-slate-400 group-hover:text-white"
          }`}
        />
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                data-cf-nav-label
                className={`flex-1 truncate ${hero ? "text-[13.5px] font-semibold tracking-tight" : ""}`}
              >
                {label}
              </span>
              {/* iter-307 — Hero card no longer renders a standalone
                  count pill; the subtitle ("25 Active · 6 Attention")
                  carries the operational signal as one composed unit. */}
              {!hero && typeof badge === "number" && badge > 0 && (
                <span
                  data-cf-nav-label
                  className="text-[10.5px] px-1.5 min-w-[20px] h-[20px] font-bold tabular-nums rounded-full inline-flex items-center justify-center"
                  style={
                    isActive
                      ? {
                          background: "rgba(15,17,38,0.55)",
                          color: "#E9D5FF",
                          boxShadow: "inset 0 0 0 1px rgba(167,139,250,0.45)",
                        }
                      : {
                          background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
                          color: "#fff",
                          boxShadow: "0 0 0 1px rgba(167,139,250,0.36), 0 4px 10px -3px rgba(124,58,237,0.55)",
                        }
                  }
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </div>
            {/* iter-307 — Hero inline subtitle (e.g. "25 Active · 6
                Attention"). Calm slate-200, no decorative dot.
                iter-308 — Opacity pulled back a notch so the
                subtitle reads as supporting telemetry, not as a
                competing headline. */}
            {hero && secondaryLine && (
              <p
                data-cf-nav-label
                data-testid="sidebar-hero-subtitle"
                className={`mt-0.5 text-[11px] font-medium tracking-tight truncate ${isActive ? "text-slate-200" : "text-slate-400"}`}
              >
                {secondaryLine}
              </p>
            )}
            {/* iter-307 — Non-hero calm KPI line. Tone defaults to
                slate; pass `subtleTone="brand"` for the Forecasting
                Centre pipeline figure so the £ value reads in the
                same brand blue used by the hero card.
                iter-308 — Visual weight on the Pipeline KPI dropped
                (opacity /65, regular weight) so it reads as soft
                ambient signal rather than a competing accent. */}
            {!hero && subtleLine && (
              <p
                data-cf-nav-label
                data-testid={`${testid}-subline`}
                className={`mt-0.5 text-[10.5px] tracking-tight truncate ${
                  subtleTone === "brand"
                    ? "text-indigo-300 font-normal"
                    : isActive ? "text-slate-200 font-normal" : "text-slate-400 font-normal"
                }`}
              >
                {subtleLine}
              </p>
            )}
          </div>
        )}
        {/* iter-alignment · P60 — Intelligent status dot.
            Renders ONLY when the item explicitly carries an
            `attention` signal (action required), not for every
            informational badge count.  In compact mode this
            makes the rail scan cleanly — dots become a
            deliberate operator signal rather than ambient noise.
            An amber tint replaces the previous violet so the
            dot reads as a call-to-action independent of the
            active-nav accent. */}
        {attention && (
          <span
            aria-hidden
            data-testid={testid ? `${testid}-attention-dot` : undefined}
            className={`absolute w-2 h-2 rounded-full ${
              collapsed ? "-top-0.5 -right-0.5" : "top-1.5 right-2"
            }`}
            style={{ background: "#F59E0B", boxShadow: "0 0 6px rgba(245,158,11,0.55)" }}
          />
        )}
      </>
    )}
  </NavLink>
);

/* ─── Canonical Information Architecture (iter-307 refinement) ───
 * Per the operator brief: every primary surface lives under one of
 * three calm sections. Deal Rooms is the only permanent hero card
 * and now sits at the top of OPERATIONS so it's the first surface
 * the operator's eye lands on. Forecasting Centre and Today carry
 * subtle KPI sublines ("£2.4m Pipeline" · "12 Priorities") — calm
 * signal, no pills, no decorative dots. Pipeline and Documents stay
 * neutral. SYSTEM renames "Analytics" → "Insights" per the brief.
 *
 *   ─ OPERATIONS ─
 *      Deal Rooms (hero) · Forecasting Centre · Today · Pipeline ·
 *      Documents
 *   ─ ORGANISATION ─
 *      Properties · Contacts · Chains
 *   ─ SYSTEM ─
 *      Insights · Settings
 *
 * No routing changes — every route resolves the way it always has.
 * "Insights" still mounts the existing /insights surface; testids
 * preserved (incl. nav-deal-rooms / nav-intelligence) so the e2e
 * suites keep resolving.
 */
const NAV_ITEMS = [
  { _label: "OPERATIONS" },
  // iter-alignment · Sidebar Reorder (2026-02-28 · pilot brief v2) —
  // Deal Rooms anchors the top of OPERATIONS again as the operator's
  // hero surface; Command Centre sits second as the daily-priority
  // landing page.  Route + `nav-*` testids preserved verbatim so
  // deep links, e2e suites and telemetry continue to resolve.
  { to: "/deal-rooms",        label: "Deal Rooms",         Icon: Monitor,      testid: "nav-deal-rooms",    hero: true, badgeKey: "dealRooms" },
  { to: "/today",             label: "Flow Centre",     Icon: Home,         testid: "nav-today",         subtleKey: "today" },
  // iter-369A · Communication Centre — office-wide inbox that
  // matches emails to the correct Deal Room automatically.
  { to: "/communication",     label: "Communication Centre", Icon: Inbox,      testid: "nav-communication" },
  { comingSoon: true,         label: "Diary",              Icon: CalendarDays, testid: "nav-diary" },
  { to: "/pipeline",          label: "Pipeline",           Icon: TrendingUp,   testid: "nav-pipeline",      subtleKey: "pipelineActive" },
  // iter-244 — Sales Progression is now the canonical Forecasting
  // Centre experience (revenue + exchange visibility). The
  // operational queue UI (legacy) lives at /sales-progression/queues
  // for backwards compatibility.
  { to: "/sales-progression", label: "Forecasting Centre", Icon: Clock,        testid: "nav-sales-progression", subtleKey: "pipeline" },
  { to: "/documents",         label: "Documents",          Icon: FileText,     testid: "nav-documents",     subtleKey: "documentsReview" },
  { _label: "ORGANISATION" },
  { to: "/properties",        label: "Properties",         Icon: Building2,    testid: "nav-properties" },
  { to: "/contacts",          label: "Contacts",           Icon: Users,        testid: "nav-people" },
  { to: "/chains",            label: "Chains",             Icon: Link2,        testid: "nav-operations" },
  { _label: "SYSTEM" },
  // iter-307 — Renamed "Analytics" → "Insights" per the brief.
  // The route + testid (`nav-intelligence`) are preserved so deep
  // links and e2e suites continue to resolve unchanged.
  { to: "/insights",          label: "Insights",           Icon: Activity,     testid: "nav-intelligence" },
  { to: "/settings",          label: "Settings",           Icon: Settings,     testid: "nav-settings" },
];

/* Legacy testid bridge — keep V1 testids that older playwright suites
 * still target. Each entry below renders an invisible 0×0 span next to
 * the nav row carrying the legacy testid, so deep-link tests resolve
 * without re-rendering the deprecated nav rows.
 */
const LEGACY_TESTID_ALIASES = {
  // iter-225B — Pipeline keeps `nav-deals` / `nav-deals-list` legacy
  // aliases (older e2e suites). `nav-sales-progression` is no
  // longer an alias because it's now a first-class sidebar entry
  // pointing at the operational SalesProgressionPage.
  "nav-pipeline":     ["nav-deals", "nav-deals-list"],
  "nav-deal-rooms":   [],
  "nav-people":       ["nav-contacts"],
  "nav-operations":   ["nav-chains"],
  "nav-intelligence": ["nav-ai-insights", "nav-analytics"],
};
/* Standalone aliases for fully-removed primary surfaces — still
 * reachable by direct URL but kept testid-resolvable. */
const STANDALONE_ALIASES = ["nav-tasks", "nav-alerts", "nav-notifications", "nav-reports", "nav-integrations"];

/* ─── Profile card (iter-293 refinement) ──────────────────────────
 * Premium glass surface · large avatar (with online dot) · name +
 * role · workspace pill + presence chip · chevron opens the
 * existing FloatingUserMenu (workspace switcher + sign out). More
 * breathing room than iter-162; no Active Deals / Forecast metrics
 * — those belong inside the workspace, not on the chrome.
 */
const ProfileCard = ({ user, office, onSignOut, collapsed }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const initials = (user?.full_name || user?.email || "?")
    .split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  const displayName = user?.full_name || user?.email?.split("@")[0] || "—";
  const roleLabel = user?.role
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1).replace(/_/g, " ")
    : "Member";
  const orgName = office || null;

  if (collapsed) {
    return (
      <div ref={anchorRef} className="relative px-2 pb-3" data-testid="sidebar-user">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="relative w-12 h-12 mx-auto rounded-2xl flex items-center justify-center transition-colors hover:bg-white/[0.04]"
          style={{
            background: "rgba(20,22,46,0.45)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
          aria-haspopup="menu"
          title={orgName || undefined}
        >
          <span
            className="w-8 h-8 rounded-full text-white text-[11px] font-bold flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #6366F1, #312E81)",
              boxShadow: "0 0 0 1px rgba(99,102,241,0.30)",
            }}
          >
            {initials}
          </span>
          <span
            aria-hidden
            className="absolute -bottom-0.5 right-1 w-2.5 h-2.5 rounded-full"
            style={{ background: "#10B981", boxShadow: "0 0 0 2px #050A18, 0 0 6px rgba(16,185,129,0.6)" }}
          />
        </button>
        {open && (
          <FloatingUserMenu anchorRef={anchorRef} onClose={() => setOpen(false)} onSignOut={onSignOut} collapsed />
        )}
      </div>
    );
  }

  return (
    <div ref={anchorRef} className="px-3 pb-3 relative" data-testid="sidebar-user">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl transition-colors hover:bg-white/[0.04] outline-none focus-visible:outline-none focus-visible:rounded-2xl focus-visible:ring-1 focus-visible:ring-[#00C6FF]/70"
        style={{
          // Subtle navy card + 1px border.
          background: "rgba(17,24,48,0.55)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="sidebar-workspace-more"
      >
        <span className="relative shrink-0">
          <span
            className="w-[42px] h-[42px] rounded-full text-white text-[13px] font-bold flex items-center justify-center tracking-[0.02em]"
            style={{
              // iter-308 — Avatar gradient pulled back from heavy
              // purple to a deeper indigo→navy stack so the profile
              // card stops competing with the Deal Rooms hero accent.
              background: "linear-gradient(135deg, #6366F1, #312E81)",
              boxShadow: "0 0 0 1px rgba(99,102,241,0.32), inset 0 1px 0 rgba(255,255,255,0.16)",
            }}
            data-testid="sidebar-user-avatar"
          >
            {initials}
          </span>
          <span
            aria-hidden
            className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
            style={{ background: "#10B981", boxShadow: "0 0 0 2px #050A18, 0 0 6px rgba(16,185,129,0.55)" }}
          />
        </span>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-[13.5px] font-semibold text-white truncate leading-tight tracking-tight" data-testid="sidebar-user-name">
            {displayName}
          </p>
          <p className="text-[11px] text-slate-400 truncate font-medium mt-0.5 leading-tight" data-testid="sidebar-user-role">
            {roleLabel}
          </p>
          {orgName && (
            <p className="text-[11px] text-slate-400 truncate mt-0.5 leading-tight" title={orgName} data-testid="sidebar-user-org">
              {orgName}
            </p>
          )}
        </div>
        <ChevronDown
          size={14}
          className={`text-slate-400 shrink-0 self-center transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <FloatingUserMenu anchorRef={anchorRef} onClose={() => setOpen(false)} onSignOut={onSignOut} />
      )}
    </div>
  );
};

const WorkspaceCard = ProfileCard; // back-compat alias for any external references

// Portalled to <body> and anchored to the account card so the compact rail's overflow-hidden
// can't clip it and the backdrop covers the whole viewport. Escape closes.
const FloatingUserMenu = ({ anchorRef, onClose, onSignOut, collapsed }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const r = anchorRef?.current?.getBoundingClientRect();
  const pos = !r ? { left: 12, bottom: 12 }
    : collapsed
      ? { left: r.right + 8, bottom: Math.max(8, window.innerHeight - r.bottom + 12) }
      : { left: r.left + 12, width: Math.max(220, r.width - 24), bottom: window.innerHeight - r.top + 8 };
  // Bottom-anchored: the menu grows upwards; cap it to the space above so nothing is clipped.
  const maxHeight = Math.max(160, window.innerHeight - (pos.bottom || 12) - 8);
  return createPortal(
  <>
    <div className="fixed inset-0 z-[90]" onClick={onClose} aria-hidden data-testid="sidebar-user-menu-backdrop" />
    <div
      className="fixed z-[91] rounded-xl overflow-y-auto cf-scroll-dark cf-fade-up"
      style={{
        ...pos,
        maxHeight,
        minWidth: 220,
        background: "rgba(15,17,38,0.96)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 16px 36px -12px rgba(0,0,0,0.55)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
      }}
      data-testid="sidebar-user-menu"
    >
      <div className="p-2 border-b border-white/[0.06]">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 px-2 py-1.5">Workspace</p>
        <div className="px-1">
          <OrgSwitcher collapsed={false} inline />
        </div>
      </div>
      <button
        type="button"
        onClick={() => { onClose(); onSignOut(); }}
        className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-[12.5px] font-semibold text-rose-300 hover:bg-rose-500/10 transition-colors"
        data-testid="sidebar-sign-out"
      >
        <LogOut size={13} />
        Sign out
      </button>
    </div>
  </>,
  document.body,
  );
};

/* ═══════════════════════════════════════════════════════════════════
   Sidebar — universal across all ChainFlow OS pages
   ─────────────────────────────────────────────────────────────────── */
export const Sidebar = ({ __isShellOwner = false }) => {
  const inShell = useContext(ShellContext);
  if (inShell && !__isShellOwner) return null;
  return <SidebarBody />;
};

const SidebarBody = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);

  /* ─── Mode state — iter-163 ─────────────────────────────────────
   * `mode` ∈ {"default", "compact"}. Two derived signals:
   *   • `userMode`   — the operator's persisted preference.
   *   • `mode`       — the *effective* mode (userMode + Deal Room
   *                    auto-immersion overlay).
   * The persisted preference NEVER changes due to auto-immersion;
   * it only changes when the operator manually toggles.
   */
  const readPersistedMode = () => {
    if (typeof window === "undefined") return "default";
    try {
      const v = window.localStorage.getItem(STORAGE_KEY_MODE);
      if (v === "default" || v === "compact") return v;
      // One-time migration from the iter-162 legacy key.
      const legacy = window.localStorage.getItem(STORAGE_KEY_LEGACY_COLLAPSED);
      if (legacy === "1") {
        window.localStorage.setItem(STORAGE_KEY_MODE, "compact");
        return "compact";
      }
      return "default";
    } catch { return "default"; }
  };
  const [userMode, setUserModeRaw] = useState(readPersistedMode);

  const location = useLocation();

  /* Session-only manual override flag — once the operator manually
   * toggles, auto-immersion in Deal Rooms steps aside for the rest of
   * this tab session. Stored in React state AND mirrored to
   * sessionStorage so a reload preserves it.
   */
  const [sessionOverride, setSessionOverride] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.sessionStorage.getItem(SESSION_KEY_USER_OVERRIDE) === "1"; }
    catch { return false; }
  });

  /* Override the persisted-write helper so manual toggles ALSO flip
   * the session override state — even when the new value matches
   * the existing userMode (otherwise React would skip the re-render
   * and the auto-immersion overlay wouldn't lift). */
  const setUserModeFromUI = useCallback((v) => {
    setUserModeRaw(v);
    try {
      window.localStorage.setItem(STORAGE_KEY_MODE, v);
      window.sessionStorage.setItem(SESSION_KEY_USER_OVERRIDE, "1");
    } catch { /* private mode — fine */ }
    setSessionOverride(true);
  }, []);

  /* Deal Room auto-immersion — when the operator lands on
   * /deal-rooms/:dealId AND hasn't manually overridden this session,
   * collapse to compact for the cinematic working canvas.
   */
  const isInDealRoom = useMemo(
    () => /^\/deal-rooms\/[^/]+/.test(location.pathname),
    [location.pathname],
  );
  /* iter-342 — Tablet viewport responsive override.
   *
   * iPad / mid-width Safari ranges (768px ≤ w ≤ 1279px) do not have
   * enough horizontal real estate for both the 260px expanded
   * sidebar AND the dense Deal Rooms five-lane grid (Property /
   * Health / Journey / Status / Dates). Without this override the
   * Property column truncates, the journey labels collide and
   * Status copy crushes into 2-line wraps.
   *
   * The override:
   *   • subscribes to a `(min-width: 768px) and (max-width: 1279px)`
   *     matchMedia query (covers all iPad orientations + most
   *     1024px Chromebooks);
   *   • forces `compact = true` regardless of `userMode` so the
   *     icon rail (76px) replaces the 260px shell — gifting the
   *     main canvas ~184px of horizontal breathing room;
   *   • leaves `userMode` (the persisted preference) untouched so
   *     desktop sessions keep their preferred expanded layout;
   *   • leaves mobile (<768px) drawer behaviour intact.
   */
  const [tabletViewport, setTabletViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.matchMedia("(min-width: 768px) and (max-width: 1279px)").matches; }
    catch { return false; }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(min-width: 768px) and (max-width: 1279px)");
    const onChange = (e) => setTabletViewport(e.matches);
    // Safari < 14 fires `addListener`; modern browsers `addEventListener`.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    // iter-342 — Belt-and-braces resize listener so embedded
    // contexts (Playwright, iframes, older WebKit) that don't
    // propagate matchMedia change events still re-evaluate the
    // override on viewport changes.
    const onResize = () => {
      const w = window.innerWidth;
      setTabletViewport(w >= 768 && w <= 1279);
    };
    window.addEventListener("resize", onResize);
    setTabletViewport(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Effective mode = persisted preference unless we're inside a deal
  // room with no manual override → then compact. iter-342: tablet
  // viewport hard-forces compact regardless of preference + Deal
  // Room overlay.
  const mode = tabletViewport
    ? "compact"
    : isInDealRoom && !sessionOverride
      ? "compact"
      : userMode;
  const compact = mode === "compact";

  /* Back-compat shims so the rest of this file (still using
   * `collapsed`) keeps working without churn. */
  const collapsed = compact;
  const setCollapsed = (v) => setUserModeFromUI(v ? "compact" : "default");

  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { notifications, deals } = useChainStore();
  const unread = notifications.filter((n) => !n.read).length;
  const { pendingCount: todayCount } = useTodaySignal(!!user);

  /* iter-alignment · P3 · Sidebar · Live Operational Metrics
   * (2026-02-28)
   * ----------------------------------------------------------------
   * Every sidebar secondary metric now derives from the canonical
   * `deals` selector already loaded into the chainStore.  Zero new
   * endpoints, zero polling, zero duplicate state — the sidebar
   * reads the same rows the destination pages read, so the numbers
   * always reconcile with the target page's totals.
   *
   * The single `useMemo` scans the deals array once and produces
   * every metric the nav rail needs (Deal Rooms · Pipeline ·
   * Documents · Forecasting Centre).  Command Centre reuses the
   * pre-existing `useTodaySignal.pendingCount` — same source of
   * truth as the Today page.
   */
  const sidebarMetrics = useMemo(() => {
    let active = 0;
    let needsAttention = 0;
    let activeOpportunities = 0;
    let awaitingReview = 0;
    for (const d of deals || []) {
      if (!d) continue;
      // iter-alignment · P0 · Command Centre · Final Pilot QA
      // (2026-03-02) — active predicate delegated to the shared
      // `isActiveDeal` SSOT so this chip reconciles exactly with
      // the Command Centre "My active deals" section.
      if (!isActiveDeal(d)) continue;
      active += 1;
      if (needsAttentionDeal(d)) needsAttention += 1;

      // Pipeline "active opportunities" = live deals BEFORE exchange —
      // the funnel a Pipeline surface would surface for conversion
      // work.  Exchanged / post-exchange deals are excluded so this
      // reconciles with the Pipeline board's own count.
      if (d.stage !== "exchanged") activeOpportunities += 1;

      // Documents awaiting review — reuses the embedded `documents`
      // array already carried by every deal (same source the
      // DocumentsPage summary reads on visit).  Status labels
      // mirror the DocumentsPage's `awaitingReview` bucket verbatim.
      const docs = Array.isArray(d.documents) ? d.documents : [];
      for (const doc of docs) {
        if (!doc) continue;
        if (doc.status === "Reviewed" || doc.status === "Needs Review") {
          awaitingReview += 1;
        }
      }
    }
    /* P0 · Data + Readiness Consistency (2026-02-28)
     * ---------------------------------------------------------
     * Pipeline Value now derives from the canonical
     * `computePipelineValue()` SSOT helper.  The previous inline
     * calculation only picked up numeric `agreedPrice` values —
     * missing the string-formatted "£745,000" API shape entirely
     * and silently reporting £0 across the pilot workspace.
     * Reading through the SSOT helper guarantees the sidebar
     * chip reconciles exactly with the Deal Rooms KPI + any
     * other surface that renders a Pipeline number. */
    const pipelineValue = computePipelineValue(deals);
    return { active, needsAttention, pipelineValue, activeOpportunities, awaitingReview };
  }, [deals]);

  /* Legacy alias — kept so the existing dot logic + Deal Rooms
   * hero secondary line don't need call-site churn. */
  const dealRoomsMetrics = useMemo(() => ({
    count:         sidebarMetrics.active,
    attention:     sidebarMetrics.needsAttention,
    pipelineValue: sidebarMetrics.pipelineValue,
  }), [sidebarMetrics]);

  /* iter-alignment · P3 · UK currency formatter.
   * ----------------------------------------------------------------
   * Sidebar values collapse to a calm compact form (`£2.4m`,
   * `£475k`) rather than the full 12-digit figure — the fine-print
   * belongs on the destination page.  When the pipeline hasn't
   * loaded yet OR is truly empty, we render `£0` because the
   * canonical answer is genuinely zero — a bare `—` would imply
   * missing data.  A bare `—` fires only when `deals` itself is
   * unavailable (undefined / null store), which is what "data
   * genuinely unavailable" means for this rail.
   */
  const _fmtCompactGbp = (v) => {
    if (v == null || Number.isNaN(v)) return "—";
    if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
    if (v >= 1_000)     return `£${Math.round(v / 1_000)}k`;
    return `£${new Intl.NumberFormat("en-GB").format(Math.round(v))}`;
  };

  const pipelineLabel = useMemo(() => {
    if (!Array.isArray(deals)) return "Pipeline —";
    return `Pipeline ${_fmtCompactGbp(sidebarMetrics.pipelineValue || 0)}`;
  }, [deals, sidebarMetrics.pipelineValue]);

  // Workspace line: the active workspace's saved name (same /api/auth/orgs source as the
  // workspace switcher and Settings); omitted when unknown — never a placeholder.
  const [workspaceName, setWorkspaceName] = useState(null);
  useEffect(() => {
    if (!user?.id) { setWorkspaceName(null); return undefined; }
    let alive = true;
    api.get("/api/auth/orgs")
      .then(({ data }) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        const active = list.find((o) => o.org_id === user.org_id) || list.find((o) => o.is_active) || null;
        setWorkspaceName(active?.org_name || null);
      })
      .catch(() => { if (alive) setWorkspaceName(null); });
    return () => { alive = false; };
  }, [user?.id, user?.org_id]);
  // Saved workspace name (organizations.name via /auth/orgs) wins everywhere.
  const office = workspaceName || user?.org_name || user?.office || null;

  const handleSignOut = async () => {
    await logout();
    toast.success("Signed out");
    nav("/login", { replace: true });
  };

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    const onToggle = () => setMobileOpen((o) => !o);
    window.addEventListener("keydown", onKey);
    window.addEventListener("cf:sidebar:toggle", onToggle);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("cf:sidebar:toggle", onToggle);
    };
  }, []);

  /* Click-outside dismiss on mobile */
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e) => {
      if (window.innerWidth >= 768) return;
      const aside = document.querySelector('aside[data-testid="cf-sidebar"]');
      if (aside && !aside.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [mobileOpen]);

  /* Edge-swipe gesture (mobile only) — unchanged from V1 */
  useEffect(() => {
    const EDGE_HIT = 18;
    const DRAWER_W = SIDEBAR_W;
    const COMMIT = DRAWER_W * 0.35;
    const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

    let startX = 0, startY = 0, active = false, captured = false, lastDx = 0, pulsed = false;
    const reset = () => { active = false; captured = false; lastDx = 0; pulsed = false; setDragging(false); setDragX(0); };

    const onTouchStart = (e) => {
      if (!isMobile() || mobileOpen) return;
      const t = e.touches[0]; if (!t) return;
      if (t.clientX > EDGE_HIT) return;
      startX = t.clientX; startY = t.clientY; active = true;
    };
    const onTouchMove = (e) => {
      if (!active) return;
      const t = e.touches[0]; if (!t) return;
      const dx = t.clientX - startX; const dy = t.clientY - startY;
      if (!captured) {
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { reset(); return; }
        if (dx > 8) { captured = true; setDragging(true); } else return;
      }
      if (e.cancelable) e.preventDefault();
      const clamped = Math.max(0, Math.min(DRAWER_W, dx));
      lastDx = clamped; setDragX(clamped);
      if (!pulsed && clamped >= COMMIT && navigator.vibrate) { try { navigator.vibrate(8); } catch { /* noop */ } pulsed = true; }
      else if (pulsed && clamped < COMMIT - 6) pulsed = false;
    };
    const onTouchEnd = () => {
      if (!active) return;
      if (captured && lastDx >= COMMIT) { setMobileOpen(true); if (navigator.vibrate) { try { navigator.vibrate(10); } catch { /* noop */ } } }
      reset();
    };
    const onTouchCancel = () => reset();

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove",  onTouchMove,  { passive: false });
    document.addEventListener("touchend",   onTouchEnd,   { passive: true });
    document.addEventListener("touchcancel", onTouchCancel, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove",  onTouchMove);
      document.removeEventListener("touchend",   onTouchEnd);
      document.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [mobileOpen]);

  /* Update shell margin variable so pages can read the current width.
   * iter-163: smooth transition baked into a single CSS custom prop.
   * iter-233: publish `effectiveWidth + CONTENT_GUTTER` so every page
   *   gets a calm 28px breathing strip between the sidebar edge and
   *   the main canvas without each page having to hand-roll padding. */
  useEffect(() => {
    const w = compact ? SIDEBAR_COMPACT_W : SIDEBAR_W;
    document.documentElement.style.setProperty("--cf-sidebar-w", `${w + CONTENT_GUTTER}px`);
  }, [compact]);

  const drawerTransform = mobileOpen
    ? "translateX(0)"
    : dragging ? `translateX(${-SIDEBAR_W + dragX}px)` : "translateX(-100%)";
  const backdropOpacity = mobileOpen ? 1 : dragging ? Math.min(1, dragX / 220) : 0;
  const effectiveWidth = compact ? SIDEBAR_COMPACT_W : SIDEBAR_W;

  return createPortal(
    <>
      {/* Mobile hamburger fallback */}
      <button
        type="button"
        data-testid="mobile-sidebar-toggle"
        onClick={() => setMobileOpen(true)}
        className="hidden fixed top-4 left-4 z-[60] w-10 h-10 rounded-xl items-center justify-center text-white"
        style={{
          background: "rgba(15,17,38,0.85)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(12px) saturate(140%)",
          WebkitBackdropFilter: "blur(12px) saturate(140%)",
        }}
        aria-label="Open navigation"
      >
        <Menu size={18} />
      </button>

      {/* Mobile edge-pull affordance */}
      {!mobileOpen && (
        <div
          aria-hidden
          data-testid="cf-edge-affordance"
          className="md:hidden pointer-events-none"
          style={{
            position: "fixed", top: "50%", left: 0, zIndex: 55,
            transform: "translateY(-50%)", width: 4, height: 56,
            borderRadius: "0 4px 4px 0",
            background: "linear-gradient(180deg, rgba(167,139,250,0) 0%, rgba(139,92,246,0.55) 50%, rgba(99,102,241,0) 100%)",
            opacity: dragging ? 0 : 0.65,
            transition: "opacity 220ms ease",
            boxShadow: "0 0 14px rgba(139,92,246,0.45)",
          }}
        />
      )}

      {/* Mobile backdrop */}
      {(mobileOpen || dragging) && (
        <div
          data-testid="mobile-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40"
          style={{
            background: `rgba(5,6,16,${0.65 * backdropOpacity})`,
            backdropFilter: `blur(${4 * backdropOpacity}px)`,
            WebkitBackdropFilter: `blur(${4 * backdropOpacity}px)`,
            transition: dragging ? "none" : "background 220ms ease",
          }}
        />
      )}

      <aside
        className="fixed left-0 top-0 h-screen h-[100dvh] z-50 flex flex-col overflow-hidden md:translate-x-0"
        style={{
          width: effectiveWidth,
          transform: drawerTransform,
          background: "#040814",
          borderRight: "1px solid rgba(99,102,241,0.16)",
          boxShadow: "inset -1px 0 0 rgba(139,92,246,0.08)",
          /* iter-alignment · P60 — Snappier width spring (200ms
             per brief) keeps the collapse feeling instant without
             losing the ease-out cushion.  `prefers-reduced-motion`
             still short-circuits the animation. */
          transition: dragging
            ? "none"
            : "width 200ms cubic-bezier(0.22, 1, 0.36, 1), transform 200ms cubic-bezier(0.16,1,0.3,1)",
        }}
        data-testid="cf-sidebar"
        data-collapsed={compact ? "1" : "0"}
        data-mode={mode}
      >
        <style>{`
          @media (min-width: 768px) {
            aside[data-testid="cf-sidebar"] {
              transform: translateX(0) !important;
              transition: width 200ms cubic-bezier(0.22, 1, 0.36, 1) !important;
            }
          }
          @media (prefers-reduced-motion: reduce) {
            aside[data-testid="cf-sidebar"] { transition: none !important; }
            aside[data-testid="cf-sidebar"] * { transition: none !important; animation: none !important; }
          }
          /* Label fade — keeps text crisp during the width spring */
          aside[data-testid="cf-sidebar"][data-mode="compact"] [data-cf-nav-label] {
            opacity: 0; transform: translateX(-4px);
            transition: opacity 120ms ease, transform 180ms ease;
            pointer-events: none;
          }
          aside[data-testid="cf-sidebar"][data-mode="default"] [data-cf-nav-label] {
            opacity: 1; transform: translateX(0);
            transition: opacity 200ms ease 40ms, transform 200ms cubic-bezier(0.22,1,0.36,1) 40ms;
          }
        `}</style>
        <Atmosphere compact={compact} />

        {/* Mobile close */}
        {mobileOpen && (
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="md:hidden absolute top-3 right-3 z-10 w-9 h-9 rounded-lg text-white flex items-center justify-center"
            style={{ background: "rgba(15,17,38,0.85)", border: "1px solid rgba(255,255,255,0.08)" }}
            aria-label="Close navigation"
            data-testid="sidebar-close"
          >
            <X size={16} />
          </button>
        )}

        {/* Brand block — iter-alignment · P60 gives the logo a
         *  little extra breathing room (pt-7 pb-6 · gap-3) so the
         *  wordmark feels less crammed against the top rail. */}
        <div
          className={`relative py-6 [@media(max-height:900px)]:py-3.5 flex items-center ${collapsed ? "px-2 justify-center" : "px-5 gap-3"} shrink-0`}
          style={{
            borderBottom: collapsed ? "none" : "1px solid rgba(255,255,255,0.04)",
          }}
        >
          {collapsed ? (
            <ChainFlowLogo variant="icon" size={30} tone="light" to="/dashboard" testid="sidebar-brand" />
          ) : (
            <Link
              to="/dashboard"
              className="inline-flex items-center shrink-0"
              aria-label="ChainFlow"
              data-testid="sidebar-brand-link"
            >
              {/* Approved logo, navy background masked to transparency (lettering ink = pure white) (675×215 PNG; visible art x34–641, y28–176).
                  54.8px box → 38px visible artwork; negative margins cancel the file's padding so the
                  symbol aligns with "Operations" and the art centres above the divider. */}
              <img
                src="/chainflow-sidebar-logo-clean.png"
                alt="ChainFlow"
                width={172}
                height={55}
                draggable={false}
                className="block select-none shrink-0"
                style={{ height: 54.8, width: "auto", maxWidth: "none", margin: "-7.1px 0 -9.7px -2.7px" }}
                data-testid="sidebar-brand"
              />
            </Link>
          )}
          {/* iter-180: top-right collapse button removed — replaced
           *  by the calm "‹ Collapse" footer at the bottom-left of
           *  the sidebar. Test-id preserved via a hidden marker so
           *  existing playwright suites resolve, but the visible
           *  affordance now lives at the bottom. */}
          {!collapsed && (
            <span
              aria-hidden
              data-testid="sidebar-collapse-toggle"
              style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}
            />
          )}
        </div>
        {collapsed && !tabletViewport && (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="hidden md:inline-flex mx-auto mb-2 w-9 h-9 rounded-lg items-center justify-center text-slate-500 hover:text-white hover:bg-white/[0.05] transition-colors"
            aria-label="Expand sidebar"
            data-testid="sidebar-expand-toggle"
          >
            <ChevronsRight size={14} />
          </button>
        )}

        {/* Flat 7-item nav · two subtle dividers · iter-162 IA */}
        <nav
          className="relative flex-1 min-h-0 overflow-y-auto cf-scroll-dark"
          style={{ paddingLeft: collapsed ? 0 : 14, paddingRight: collapsed ? 0 : 14 }}
          data-testid="sidebar-nav"
        >
          <ul className="pt-1 space-y-0.5">
            {NAV_ITEMS.map((it, idx) => {
              if (it._divider) {
                return <SectionDivider key={`div-${idx}`} collapsed={collapsed} />;
              }
              if (it._label) {
                return <SectionLabel key={`lbl-${idx}`} label={it._label} collapsed={collapsed} />;
              }
              if (it.comingSoon) {
                return (
                  <li key={it.testid}>
                    <ComingSoonNavItem label={it.label} Icon={it.Icon} testid={it.testid} collapsed={collapsed} />
                  </li>
                );
              }
              // Drive badge values from chainStore where applicable.
              let badge;
              let secondaryLine;
              let subtleLine;
              let subtleTone;
              if (it.badgeKey === "unread") badge = unread;
              if (it.badgeKey === "today")  badge = todayCount;
              if (it.badgeKey === "dealRooms") {
                // iter-alignment · P3 · Sidebar · Live Operational
                // Metrics (2026-02-28) — hero secondary line reads
                // literally from the canonical `sidebarMetrics`
                // slice so it always reconciles with the Deal Rooms
                // KPI strip on the destination page.  Copy per the
                // brief: "{active} Active • {needsAttention} Need
                // attention".  Falls back to "All on track" when
                // nothing needs attention.  A bare "—" fires only
                // when the deals array itself is genuinely
                // unavailable (undefined store).
                badge = undefined;
                if (!Array.isArray(deals)) {
                  secondaryLine = "—";
                } else if (sidebarMetrics.needsAttention > 0) {
                  secondaryLine = `${sidebarMetrics.active} Active • ${sidebarMetrics.needsAttention} Needs Attention`;
                } else {
                  secondaryLine = `${sidebarMetrics.active} Active • All on track`;
                }
              }
              // iter-307 — Subtle KPI sublines for non-hero items.
              // Calm tone by default; Forecasting Centre uses the
              // brand-blue accent so the £ value reads as a real
              // money figure rather than a label.
              if (it.subtleKey === "pipeline") {
                subtleLine = pipelineLabel;
                subtleTone = "brand";
              }
              if (it.subtleKey === "today") {
                // iter-alignment · P3 · Sidebar (2026-02-28) — copy
                // aligned with the brief: `Priorities today`.
                subtleLine = `${todayCount || 0} Priorities today`;
              }
              /* iter-alignment · P3 · Sidebar · Live Operational
               * Metrics (2026-02-28) — Pipeline shows the live
               * active-opportunity count.  Deals array unavailable
               * → "—"; zero opportunities → "0 Active opportunities"
               * (never hidden) so the operator always knows the
               * board is truly empty rather than not-loaded. */
              if (it.subtleKey === "pipelineActive") {
                if (!Array.isArray(deals)) {
                  subtleLine = "—";
                } else {
                  subtleLine = `${sidebarMetrics.activeOpportunities} Active opportunities`;
                }
              }
              /* iter-alignment · P3 · Sidebar · Live Operational
               * Metrics (2026-02-28) — Documents shows the awaiting-
               * review count.  Hidden when zero per the brief so
               * the rail stays calm on a healthy day.  A bare "—"
               * NEVER appears here because the field is either
               * present-with-a-value or absent — matching the
               * brief's "hidden when zero" rule. */
              if (it.subtleKey === "documentsReview") {
                if (Array.isArray(deals) && sidebarMetrics.awaitingReview > 0) {
                  subtleLine = `${sidebarMetrics.awaitingReview} Awaiting review`;
                }
              }
              const aliases = LEGACY_TESTID_ALIASES[it.testid] || [];
              /* iter-alignment · P60 — Intelligent attention signal.
               * The status dot now fires ONLY when action is required
               * — the Deal Rooms hero surfaces one when at least one
               * deal is flagged for attention, and Today surfaces one
               * when there are outstanding priorities.  Other items
               * stay dot-free even if they carry an informational
               * count (badge). */
              let attention = false;
              if (it.badgeKey === "dealRooms") attention = (dealRoomsMetrics.attention || 0) > 0;
              if (it.subtleKey === "today")   attention = (todayCount || 0) > 0;
              return (
                <li key={it.to} className="relative">
                  <NavItem
                    to={it.to}
                    label={it.label}
                    Icon={it.Icon}
                    testid={it.testid}
                    end={it.end}
                    badge={badge}
                    hero={!!it.hero}
                    secondaryLine={secondaryLine}
                    subtleLine={subtleLine}
                    subtleTone={subtleTone}
                    collapsed={collapsed}
                    attention={attention}
                  />
                  {/* Legacy testid aliases — invisible 0×0 markers so existing
                      playwright suites resolve nav-deals / nav-contacts / etc. */}
                  {aliases.map((alias) => (
                    <span
                      key={alias}
                      aria-hidden
                      data-testid={alias}
                      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}
                    />
                  ))}
                </li>
              );
            })}
            {/* Standalone aliases for removed primary surfaces — still
                reachable by direct URL, kept testid-resolvable. */}
            {STANDALONE_ALIASES.map((alias) => (
              <span
                key={alias}
                aria-hidden
                data-testid={alias}
                style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" }}
              />
            ))}
          </ul>
        </nav>

        {/* Bottom region — Profile card + Collapse/Expand footer.
         *  iter-180 placed the calm "‹ Collapse" affordance here.
         *  iter-231 adds the symmetric "›" Expand affordance in the
         *  same spatial slot when the sidebar is collapsed — so the
         *  operator always finds the toggle in the same place,
         *  regardless of state. Mirrors stroke weight, hover tone,
         *  and tap area for a calm, discoverable affordance. */}
        {/* iter-alignment · P0 · Sidebar Viewport Pin (2026-03-06)
         *  Footer band (ProfileCard + Collapse) is pinned to the
         *  bottom via the parent `<aside>` flex-column + this
         *  band's `shrink-0`. Respect the browser/device safe
         *  area inset (iPhone home indicator, iPad chrome) so
         *  the Collapse row never renders under a rounded edge
         *  or bezel. `pb-[env(...)]` resolves to 0 on desktop —
         *  no visual change on non-mobile viewports. */}
        <div className="relative shrink-0 pb-[env(safe-area-inset-bottom)]">
          <ProfileCard user={user} office={office} onSignOut={handleSignOut} collapsed={collapsed} />
          {!collapsed && (
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="hidden md:flex w-[calc(100%-24px)] mx-3 items-center gap-2 px-2 pb-4 pt-3 [@media(max-height:900px)]:pb-2 [@media(max-height:900px)]:pt-2 border-t border-white/[0.06] text-[11.5px] font-medium text-slate-400 hover:text-slate-200 transition-colors"
              aria-label="Collapse sidebar"
              data-testid="sidebar-collapse-footer"
            >
              <ChevronsLeft size={13} />
              Collapse
            </button>
          )}
          {collapsed && !tabletViewport && (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="hidden md:flex w-full items-center justify-center pb-4 pt-1 text-slate-500 hover:text-slate-300 transition-colors"
              aria-label="Expand sidebar"
              title="Expand sidebar"
              data-testid="sidebar-expand-footer"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/[0.05]">
                <ChevronsRight size={13} />
              </span>
            </button>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
};
